import React, { useRef } from 'react'
import { cn } from '../../utils/cn'
import { useModal, useSheetMode, useVisualViewport } from '../../hooks/useModal'

interface DialogProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showCloseButton?: boolean
  /** Feste Fußzeile (z. B. Abbrechen/Speichern): am Handy immer sichtbar, auch bei offener Tastatur. */
  footer?: React.ReactNode
  /** CSS-Selektor des zuerst fokussierten Elements (Standard: erstes fokussierbares). */
  initialFocus?: string
}

const widthClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[95vw]'
}

/**
 * Dialog. Ab 768 px zentriertes Fenster; darunter Vollbild-Sheet von unten mit fester Kopfzeile (Titel, Schließen 48 px),
 * scrollendem Inhalt und fester Fußzeile (`footer`). Esc schließt, Fokusfalle, Fokus-Rückgabe, Hintergrund-Scroll gesperrt.
 */
export function Dialog({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'lg',
  showCloseButton = true,
  footer,
  initialFocus
}: DialogProps) {
  const ref = useRef<HTMLDivElement>(null)
  const sheet = useSheetMode()
  const vv = useVisualViewport(isOpen && sheet)
  useModal(isOpen, ref, onClose, initialFocus)
  if (!isOpen) return null

  const titleId = 'dlg-title-' + title.replace(/\W+/g, '-')
  const sheetStyle: React.CSSProperties | undefined = sheet && vv ? { height: vv.height, top: vv.top, bottom: 'auto' } : undefined

  return (
    <div className="fixed inset-0 z-50">
      <div className="ui-overlay absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        data-ui-modal=""
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={sheetStyle}
        className={cn(
          'ui-pop flex flex-col outline-none',
          sheet
            ? 'fixed inset-x-0 bottom-0 top-0 !rounded-none !border-0 ui-sheet-in'
            : cn('absolute left-1/2 top-1/2 w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] -translate-x-1/2 -translate-y-1/2', widthClasses[maxWidth])
        )}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-dark-border px-5 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] sm:px-6 md:pt-2">
          <h2 id={titleId} className="min-w-0 break-words text-lg font-semibold text-dark-text sm:text-xl">{title}</h2>
          {showCloseButton && (
            <button type="button" onClick={onClose} aria-label="Schließen" className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-dark-text-muted transition-colors hover:bg-dark-bg-tertiary hover:text-dark-text md:h-11 md:w-11">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5 sm:p-6">{children}</div>
        {footer && (
          <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-dark-border px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 md:flex-row md:justify-end md:pb-3 [&>button]:min-h-12 md:[&>button]:min-h-11">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  title: string
  message: string
  onConfirm: () => void
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'primary'
  loading?: boolean
}

export function ConfirmDialog({
  isOpen,
  onClose,
  title,
  message,
  onConfirm,
  confirmText = 'Bestätigen',
  cancelText = 'Abbrechen',
  variant = 'danger',
  loading = false
}: ConfirmDialogProps) {
  if (!isOpen) return null

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      maxWidth="sm"
      initialFocus="[data-abbrechen]"
      footer={
        <>
          <button data-abbrechen onClick={onClose} disabled={loading} className="btn-secondary">{cancelText}</button>
          <button onClick={onConfirm} disabled={loading} className={variant === 'danger' ? 'btn-danger' : 'btn-primary'}>
            {loading ? 'Lädt...' : confirmText}
          </button>
        </>
      }
    >
      <p className="text-dark-text-muted">{message}</p>
    </Dialog>
  )
}
